import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { leadsApi, type LeadPlan } from '../lib/api.js'
import { Button, Input, Modal, Select } from './ui/index.js'
import { useI18n } from '../lib/i18n.js'
import { countryOptions } from '../lib/countries.js'

/**
 * "Mais informações" sobre Small Business / Pro (#293): os planos completos
 * não têm preço público nem checkout — o dono deixa o contato e a equipe
 * comercial da PRATZU responde. Envia para POST /public/leads (mesmo contrato
 * da landing), com Turnstile e honeypot. Nome, e-mail e país (o país filtra a
 * proposta: vem preenchido com o do restaurante e pode ser trocado);
 * consentimento `lead-consent-v1` com o mesmo texto da landing — o aviso de
 * dados (consent_note) fica abaixo do checkbox e não faz parte do texto aceito.
 */

/** Sitekey de teste da Cloudflare (sempre aprova) — só fora de produção. */
export const TURNSTILE_TEST_SITEKEY = '1x00000000000000000000AA'
export const LEAD_CONSENT_VERSION = 'lead-consent-v1'

/**
 * Hosts de produção do painel: sem sitekey configurada, o modal fica
 * indisponível (nunca a sitekey de teste). Qualquer outro host (p.ex.
 * `console-staging.pratzu.com`, localhost) é staging/dev e cai na sitekey de
 * teste se faltar a env.
 */
const PRODUCTION_ADMIN_HOSTS = /^(?:admin|console)\.(?:pratzu|asknamba)\.com$/

export function turnstileSitekey(
  configured: string | undefined,
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : '',
): string {
  if (configured) return configured
  return PRODUCTION_ADMIN_HOSTS.test(hostname) ? '' : TURNSTILE_TEST_SITEKEY
}

type TurnstileApi = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string
  remove?: (id: string) => void
}

function loadTurnstile(): Promise<TurnstileApi> {
  const w = window as unknown as { turnstile?: TurnstileApi }
  if (w.turnstile) return Promise.resolve(w.turnstile)
  return new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    s.async = true
    s.onload = () => (w.turnstile ? resolve(w.turnstile) : reject(new Error('turnstile')))
    s.onerror = () => reject(new Error('turnstile'))
    document.head.appendChild(s)
  })
}

export function LeadModal({
  plan,
  country,
  onClose,
}: {
  plan: Extract<LeadPlan, 'small_business' | 'pro'> | null
  country: string
  onClose: () => void
}) {
  const { t, lang } = useI18n()
  const sitekey = turnstileSitekey(import.meta.env.VITE_TURNSTILE_SITEKEY as string | undefined)
  const [form, setForm] = useState({ contact_name: '', email: '', country })
  // lista da API de países da #313; nomes via Intl no idioma do painel
  const { data: lists } = useQuery({
    queryKey: ['public-countries'],
    queryFn: leadsApi.countries,
    enabled: !!plan,
    staleTime: 5 * 60_000,
  })
  const countries = useMemo(
    () => countryOptions([lists?.signup, lists?.homologated], lang, [country]),
    [lists, lang, country],
  )
  const [consent, setConsent] = useState(false)
  const [website, setWebsite] = useState('')
  const [token, setToken] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [msg, setMsg] = useState<string | null>(null)
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  // o modal fica montado na página: a cada abertura, volta ao país do restaurante
  useEffect(() => {
    if (plan) setForm((f) => ({ ...f, country }))
  }, [plan, country])

  useEffect(() => {
    if (!plan || !sitekey || !box.current) return
    let id: string | undefined
    let api: TurnstileApi | undefined
    loadTurnstile()
      .then((ts) => {
        api = ts
        if (box.current)
          id = ts.render(box.current, {
            sitekey,
            callback: (tk: string) => setToken(tk),
            'expired-callback': () => setToken(''),
          })
      })
      .catch(() => setMsg(t('admin.lead_modal.turnstile_error')))
    return () => {
      if (id && api?.remove) api.remove(id)
    }
  }, [plan, sitekey, t])

  if (!plan) return null

  const field = (k: keyof typeof form) => ({
    value: form[k],
    ...(errors[k] ? { error: t(`admin.lead_modal.err_${errors[k]}`) } : {}),
    onChange: (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value }),
  })

  async function submit() {
    setMsg(null)
    const local: Record<string, string> = {}
    if (!form.contact_name.trim()) local.contact_name = 'required'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) local.email = 'email'
    if (!form.country) local.country = 'required'
    else if (!/^[A-Z]{2}$/.test(form.country)) local.country = 'invalid'
    if (!consent) local.consent = 'required'
    setErrors(local)
    if (Object.keys(local).length) return
    if (!token) return setMsg(t('admin.lead_modal.turnstile_wait'))
    setBusy(true)
    try {
      const r = await leadsApi.create({
        plan: plan!,
        contact_name: form.contact_name.trim(),
        email: form.email.trim(),
        country: form.country,
        locale: lang,
        consent: { text_version: LEAD_CONSENT_VERSION, at: new Date().toISOString() },
        source_page: 'console:/users',
        turnstile_token: token,
        website,
      })
      if (r.ok) return setSent(true)
      if (r.code === 'VALIDATION_ERROR') {
        setErrors(r.fields ?? {})
        return setMsg(t('admin.lead_modal.err_validation'))
      }
      if (r.code === 'TURNSTILE_FAILED') return setMsg(t('admin.lead_modal.turnstile_error'))
      if (r.code === 'RATE_LIMITED') return setMsg(t('admin.lead_modal.err_rate'))
      // 503: produção sem TURNSTILE_SECRET_KEY (fail-closed na API)
      if (r.code === 'LEADS_UNAVAILABLE') return setMsg(t('admin.lead_modal.unavailable'))
      setMsg(t('admin.lead_modal.err_generic'))
    } catch {
      setMsg(t('admin.lead_modal.err_generic'))
    } finally {
      setBusy(false)
    }
  }

  const planName = t(plan === 'pro' ? 'admin.lead_modal.plan_pro' : 'admin.lead_modal.plan_sb')
  return (
    <Modal
      open
      onClose={onClose}
      title={t('admin.lead_modal.title', { plan: planName })}
      footer={
        sent ? (
          <Button onClick={onClose}>{t('admin.lead_modal.close')}</Button>
        ) : (
          <Button
            onClick={submit}
            loading={busy}
            disabled={!sitekey}
            data-testid="lead-modal-submit"
          >
            {t('admin.lead_modal.send')}
          </Button>
        )
      }
    >
      {sent ? (
        <p className="text-sm text-gray-700" data-testid="lead-modal-sent">
          {t('admin.lead_modal.sent')}
        </p>
      ) : (
        <div className="space-y-3" data-testid="lead-modal">
          <p className="text-sm text-gray-600">{t('admin.lead_modal.intro')}</p>
          <Input label={t('admin.lead_modal.name')} {...field('contact_name')} />
          <Input type="email" label={t('admin.lead_modal.email')} {...field('email')} />
          <Select
            label={t('admin.lead_modal.country')}
            id="lead-modal-country"
            data-testid="lead-modal-country"
            required
            {...field('country')}
            options={[...(form.country ? [] : [{ value: '', label: '—' }]), ...countries]}
          />
          {/* honeypot: invisível para humanos */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden
            className="hidden"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
          <label className="flex items-start gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              aria-describedby="lead-consent-note"
              data-testid="lead-modal-consent"
            />
            <span>{t('admin.lead_modal.consent')}</span>
          </label>
          <p
            id="lead-consent-note"
            className="text-xs text-gray-500"
            data-testid="lead-modal-consent-note"
          >
            {t('admin.lead_modal.consent_note')}
          </p>
          {errors.consent && (
            <p className="text-xs text-red-600">{t('admin.lead_modal.err_consent')}</p>
          )}
          {sitekey ? (
            <div ref={box} data-testid="lead-modal-turnstile" />
          ) : (
            <p className="text-sm text-amber-700">{t('admin.lead_modal.unavailable')}</p>
          )}
          {msg && (
            <p className="text-sm text-red-600" role="alert">
              {msg}
            </p>
          )}
        </div>
      )}
    </Modal>
  )
}
